// Dathent API Client
// Centralized API client for all backend communication

export class DathentAPIError extends Error {
  constructor(message, code, status, details) {
    super(message);
    this.name = 'DathentAPIError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const RATE_LIMIT_RETRY_MAX_DELAY_MS = 4000;

function defaultBaseURL() {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'app.dathent.ai' || hostname.endsWith('.dathent.ai') || hostname.endsWith('.pages.dev')) {
      return 'https://api.dathent.ai';
    }
  }

  return 'http://localhost:8787';
}

export class DathentClient {
  constructor(options = {}) {
    this.baseURL = options.baseURL || defaultBaseURL();
    this.onAuthError = options.onAuthError || (() => {});
    this.token = null;
    // GET response cache: key=method+url, value={ t: msExpire, v: data }
    // Optional per-call TTL via options.cacheTTL (ms). Cleared on mutating
    // calls via this.invalidateCache(prefix).
    this._cache = new Map();
    // In-flight GET dedupe: key=cache key, value=Promise. Concurrent calls
    // to the same URL return the same promise instead of re-fetching.
    this._inFlight = new Map();
    
    // Initialize sub-clients
    this.auth = new AuthClient(this);
    this.profile = new ProfileClient(this);
    this.onboarding = new OnboardingClient(this);
    this.strategy = new StrategyClient(this);
    this.content = new ContentClient(this);
    this.scheduler = new SchedulerClient(this);
    this.socialAccounts = new SocialAccountsClient(this);
    this.autopilot = new AutopilotClient(this);
    this.notifications = new NotificationsClient(this);
    this.billing = new BillingClient(this);
    this.apiKeys = new ApiKeysClient(this);
    this.platforms = new PlatformsClient(this);
    this.carousel = new CarouselClient(this);
    this.assistant = new AssistantClient(this);
    this.analytics = new AnalyticsClient(this);
    this.threads = new ThreadsClient(this);
    this.instagram = new InstagramClient(this);
    this.referral = new ReferralClient(this);
    this.adminReferral = new AdminReferralClient(this);
  }

  setToken(token) {
    // CRITICAL: token change means user identity change (login/logout/switch
    // accounts). The GET response cache is keyed by URL only — not user —
    // so we MUST flush it on every token transition to avoid serving the
    // previous user's cached responses to the new session. Same for in-flight
    // dedupe map. This prevents cross-session data leaks (e.g. Dashboard
    // posts-summary, Library, analytics) when a user signs out and another
    // user signs in within the cache TTL window.
    const tokenChanged = this.token !== token;
    this.token = token;
    if (tokenChanged) {
      this._cache.clear();
      this._inFlight.clear();
    }
    if (token) {
      localStorage.setItem('dathent_token', token);
    } else {
      localStorage.removeItem('dathent_token');
    }
  }

  // Public helper for explicit cache flush (used by AuthContext on sign-in
  // and sign-out flows to ensure no stale data carries over).
  clearCache() {
    this._cache.clear();
    this._inFlight.clear();
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('dathent_token');
    }
    return this.token;
  }

  async request(method, path, data = null, options = {}) {
    const url = `${this.baseURL}${path}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      method,
      headers,
      credentials: options.credentials || 'include',
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.body = JSON.stringify(data);
    }

    try {
      let response = await fetch(url, config);
      let didRateLimitRetry = false;
      let retryDelayMs = 0;

      if (response.status === 429 && options.retryRateLimit !== false) {
        retryDelayMs = await rateLimitRetryDelayMs(response.clone());
        if (retryDelayMs <= RATE_LIMIT_RETRY_MAX_DELAY_MS) {
          didRateLimitRetry = true;
          await sleep(retryDelayMs);
          response = await fetch(url, config);
        }
      }
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let responseData;
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = { message: await response.text() };
      }

      if (!response.ok) {
        const apiError = responseData?.error || responseData || {};
        const errorMessage = typeof apiError === 'string'
          ? responseData?.message || apiError
          : apiError.message || responseData?.message || 'API request failed';
        const errorCode = typeof apiError === 'string'
          ? apiError
          : apiError.code || responseData?.code || 'UNKNOWN_ERROR';
        // Handle auth errors
        if (response.status === 401) {
          this.setToken(null);
          this.onAuthError();
        }

        const details = typeof apiError === 'string' ? responseData?.details || null : apiError.details || responseData?.details || null;
        const enrichedDetails = {
          ...(details && typeof details === 'object' && !Array.isArray(details) ? details : { details }),
          method,
          path,
          traceId: response.headers.get('x-trace-id') || null,
          retryAfterSeconds: retryAfterSeconds(response, details),
          retryAttempted: didRateLimitRetry,
        };

        throw new DathentAPIError(
          errorMessage,
          errorCode,
          response.status,
          enrichedDetails
        );
      }

      return responseData?.ok === true && Object.prototype.hasOwnProperty.call(responseData, 'data')
        ? responseData.data
        : responseData;
    } catch (error) {
      if (error instanceof DathentAPIError) {
        throw error;
      }
      
      // Network or other errors
      throw new DathentAPIError(
        error.message || 'Network error',
        'NETWORK_ERROR',
        0,
        { originalError: error }
      );
    }
  }

  // Cached + deduplicated GET. Pass options.cacheTTL (ms) to enable caching;
  // omit it for a plain request. Concurrent calls with the same key share
  // a single in-flight promise so a fast navigation burst doesn't fan out.
  get(path, options) {
    const ttl = options?.cacheTTL;
    const key = `GET ${path}`;
    const now = Date.now();
    if (ttl > 0) {
      const cached = this._cache.get(key);
      if (cached && cached.t > now) return Promise.resolve(cached.v);
    }
    if (this._inFlight.has(key)) return this._inFlight.get(key);
    const promise = this.request('GET', path, null, options)
      .then((value) => {
        if (ttl > 0) this._cache.set(key, { t: now + ttl, v: value });
        this._inFlight.delete(key);
        return value;
      })
      .catch((err) => {
        this._inFlight.delete(key);
        throw err;
      });
    this._inFlight.set(key, promise);
    return promise;
  }

  // Mutating verbs invalidate any cached GETs whose URL begins with the
  // supplied prefix(es). Sub-clients can also call invalidateCache directly
  // after any custom action.
  post(path, data, options) {
    return this.request('POST', path, data, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }
  put(path, data, options) {
    return this.request('PUT', path, data, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }
  patch(path, data, options) {
    return this.request('PATCH', path, data, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }
  delete(path, options) {
    return this.request('DELETE', path, null, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }

  invalidateCache(prefixes) {
    if (!prefixes) return;
    const list = Array.isArray(prefixes) ? prefixes : [prefixes];
    for (const prefix of list) {
      // Normalise: strip query, drop leading slash differences
      const norm = String(prefix).split('?')[0];
      for (const key of this._cache.keys()) {
        // key is "GET /api/whatever"
        if (key.startsWith(`GET ${norm}`)) this._cache.delete(key);
      }
    }
  }

  url(path) {
    return `${this.baseURL}${path}`;
  }
}

function retryAfterSeconds(response, details) {
  const header = Number(response.headers.get('Retry-After'));
  if (Number.isFinite(header) && header >= 0) return header;
  const detailValue = Number(details?.retryAfterSeconds);
  return Number.isFinite(detailValue) && detailValue >= 0 ? detailValue : null;
}

async function rateLimitRetryDelayMs(response) {
  let details = null;
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const body = await response.json();
      details = body?.error?.details || body?.details || null;
    }
  } catch {
    details = null;
  }
  const seconds = retryAfterSeconds(response, details);
  if (seconds === null) return 750;
  return Math.max(250, Math.ceil(seconds * 1000));
}

function sleep(ms) {
  return new Promise(resolve => globalThis.setTimeout(resolve, ms));
}

/* Read a File/Blob and return its body as a base64 string (no data: prefix).
   Used for the JSON-based image upload endpoint — multipart is awkward
   client-side and base64 keeps the request shape consistent with the rest
   of the API. */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/* Best-effort image dimensions read (decodes via an Image element). The
   server can usually figure dims out itself, but passing them lets us
   skip a roundtrip and surface dimension errors before the upload. */
function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const out = { width: img.naturalWidth || 0, height: img.naturalHeight || 0 };
      URL.revokeObjectURL(url);
      resolve(out);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image'));
    };
    img.src = url;
  });
}

// Auth Client
class AuthClient {
  constructor(client) {
    this.client = client;
  }

  async login(email, password) {
    const response = await this.client.post('/api/auth/login', { email, password });
    if (response.requiresMfa || response.requiresMfaSetup) {
      return response;
    }
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async verifyRequiredMfaSetup(attemptId, code) {
    const response = await this.client.post('/api/auth/mfa/totp/login/setup/verify', { attemptId, code });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response;
  }

  async verifyMfaLogin(attemptId, code) {
    const response = await this.client.post('/api/auth/mfa/totp/login/verify', { attemptId, code });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async verifyMfaBackupCode(attemptId, backupCode) {
    const response = await this.client.post('/api/auth/mfa/backup-code/login/verify', { attemptId, backupCode });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async sendMfaEmailCode(attemptId) {
    return this.client.post('/api/auth/mfa/email/login/send', { attemptId });
  }

  async verifyMfaEmailCode(attemptId, code) {
    const response = await this.client.post('/api/auth/mfa/email/login/verify', { attemptId, code });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async getMfaStatus() {
    return this.client.get('/api/auth/mfa/status');
  }

  async startMfaSetup() {
    return this.client.post('/api/auth/mfa/totp/setup/start');
  }

  async verifyMfaSetup(code) {
    return this.client.post('/api/auth/mfa/totp/setup/verify', { code });
  }

  async disableMfa(password, code) {
    return this.client.post('/api/auth/mfa/totp/disable', { password, code });
  }

  async regenerateMfaBackupCodes(password, code) {
    return this.client.post('/api/auth/mfa/backup-codes/regenerate', { password, code });
  }

  async signup(email, password, name) {
    const response = await this.client.post('/api/auth/signup', { email, password, name });
    if (response.requiresMfaSetup) {
      return response;
    }
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async logout() {
    try {
      await this.client.post('/api/auth/logout');
    } finally {
      this.client.setToken(null);
    }
  }

  async getCurrentUser() {
    const response = await this.client.get('/api/auth/me');
    return response.user || response;
  }

  async refreshToken() {
    const response = await this.client.post('/api/auth/refresh');
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response;
  }

  async forgotPassword(email) {
    return this.client.post('/api/auth/password/forgot', { email });
  }

  async resetPassword(token, password) {
    return this.client.post('/api/auth/password/reset', { token, password });
  }
}

// Profile Client
class ProfileClient {
  constructor(client) {
    this.client = client;
  }

  async getBrandProfile() {
    return this.client.get('/api/brand-profile', { cacheTTL: 5 * 60 * 1000 });
  }

  async updateBrandProfile(data) {
    return this.client.put('/api/brand-profile', data);
  }

  async addBrandReference(data) {
    return this.client.post('/api/brand-profile/references', data);
  }

  async getPublishingPreferences() {
    return this.client.get('/api/profile/publishing-preferences', { cacheTTL: 2 * 60 * 1000 });
  }

  async updatePublishingPreferences(data) {
    return this.client.put('/api/profile/publishing-preferences', data);
  }

  async getPlatformPreferences() {
    return this.client.get('/api/profile/platform-preferences');
  }

  async updatePlatformPreference(data) {
    return this.client.put('/api/profile/platform-preferences', data);
  }

  async rebuildStrategy() {
    return this.client.post('/api/profile/rebuild-strategy');
  }
}

// Onboarding Client
class OnboardingClient {
  constructor(client) {
    this.client = client;
  }

  async start() {
    return this.client.post('/api/onboarding/start');
  }

  async saveMessage({ stepKey, question = null, answerText, nextStep = null, extracted = null }) {
    return this.client.post('/api/onboarding/message', { stepKey, question, answerText, nextStep, extracted });
  }

  async sendAiMessage({ answerText, currentStep, clientTurnId = null }) {
    return this.client.post('/api/onboarding/ai/message', { answerText, currentStep, clientTurnId });
  }

  async analyzeWebsite({ url }) {
    return this.client.post('/api/onboarding/website/analyze', { url });
  }

  async addReference(data) {
    return this.client.post('/api/onboarding/references', data);
  }

  async complete() {
    return this.client.post('/api/onboarding/complete');
  }

  async getStatus() {
    return this.client.get('/api/onboarding/status');
  }
}

// Strategy Client
class StrategyClient {
  constructor(client) {
    this.client = client;
  }

  async generate(data = {}) {
    return this.client.post('/api/strategy/generate', data);
  }

  async get(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.client.get(`/api/strategy${query ? `?${query}` : ''}`, { cacheTTL: 5 * 60 * 1000 });
  }

  async generateWeeklyPlan(data) {
    return this.client.post('/api/strategy/weekly-plan', data);
  }

  async generateMonthlyPlan(data = {}) {
    return this.client.post('/api/strategy/monthly-plan', data);
  }
}

// Content Client
class ContentClient {
  constructor(client) {
    this.client = client;
  }

  async generate(params) {
    return this.client.post('/api/content/generate', params);
  }

  async generateFirstPost(params = {}) {
    return this.client.post('/api/content/first-post', params);
  }

  async createBatch(params) {
    return this.client.post('/api/content/batches', params);
  }

  async listBatches(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/content/batches${query ? `?${query}` : ''}`, { cacheTTL: 30 * 1000 });
  }

  async getBatch(id) {
    return this.client.get(`/api/content/batches/${id}`);
  }

  async appendBatch(id, data = {}) {
    return this.client.post(`/api/content/batches/${id}/append`, data);
  }

  async archiveBatch(id) {
    return this.client.post(`/api/content/batches/${id}/archive`);
  }

  async pauseBatch(id) {
    return this.client.post(`/api/content/batches/${id}/pause`);
  }

  async rescheduleBatch(id, data = {}) {
    return this.client.post(`/api/content/batches/${id}/reschedule`, data);
  }

  async list(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/content${query ? `?${query}` : ''}`);
  }

  async feed(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/content/feed${query ? `?${query}` : ''}`);
  }

  async get(id) {
    // 30s cache — PostPreview + EditPost frequently fetch the same content
    return this.client.get(`/api/content/${encodeURIComponent(id)}`, { cacheTTL: 30 * 1000 });
  }

  async refreshMetrics(id, data = {}) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/metrics/refresh`, data);
  }

  async adaptPlatforms(id, data = {}) {
    return this.client.post(`/api/content/${id}/adapt-platforms`, data);
  }

  async regenerate(id, data = {}) {
    return this.client.post(`/api/content/${id}/regenerate`, data, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`, `/api/content`],
    });
  }

  /* Image-only regeneration. Keeps caption/copy, replaces carousel
     slides with freshly-generated images using the post's existing
     prompt + language. Returns the refreshed content payload.
     Optional fields the backend accepts:
       instructions  — user's free-text note on what to change
       language      — ISO override (e.g. "lt") for image text
       slideIndex    — 1-based: regenerate only ONE slide of a carousel
     All three are passed through to the visual prompt.
   */
  async regenerateImage(id, data = {}) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/regenerate-image`, data, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`, `/api/content`],
    });
  }

  /* Persist a slide reorder for a content item's active version. Frontend
     uses this on drag-drop autosave — there's no Save button on the media
     tab; every reorder gesture fires one POST. */
  async reorderSlides(id, slideIds) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/slides/reorder`, { slideIds }, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`],
    });
  }

  /* Upload a user-supplied image and bind it to a carousel slide.
     `file` is a File / Blob; we read it as base64 client-side so the
     request is plain JSON (multipart is awkward to construct from fetch).
     `slideIndex` is 1-based — the slide to REPLACE. EditPost passes
     the currently-selected slide so the upload lands where the user
     expects, not always slide 1. */
  async uploadCustomImage(id, file, { slideIndex = 1 } = {}) {
    const base64 = await fileToBase64(file);
    const dims = await readImageDimensions(file).catch(() => ({ width: 0, height: 0 }));
    return this.client.post(`/api/content/${encodeURIComponent(id)}/upload-image`, {
      imageBase64: base64,
      width: dims.width,
      height: dims.height,
      slideIndex
    }, {
      invalidate: [`/api/content/${id}`, `/api/content`],
    });
  }

  /* Delete a single carousel slide from a content item's active version.
     Backend re-packs remaining slides' indices back to 1..N. Refuses to
     delete the last slide on image-required formats — surface that error
     to the user via the standard error path. */
  async deleteSlide(id, slideId) {
    return this.client.delete(`/api/content/${encodeURIComponent(id)}/slides/${encodeURIComponent(slideId)}`, {
      invalidate: [`/api/content/${id}`, `/api/content`],
    });
  }

  async listVersions(id) {
    return this.client.get(`/api/content/${id}/versions`);
  }

  async activateVersion(id, versionId) {
    return this.client.post(`/api/content/${id}/versions/${versionId}/activate`);
  }

  async createManualEdit(id, data) {
    return this.client.post(`/api/content/${id}/manual-edit`, data);
  }

  async archive(id) {
    return this.client.post(`/api/content/${id}/archive`);
  }

  async restore(id) {
    return this.client.post(`/api/content/${id}/restore`);
  }

  async duplicate(id) {
    return this.client.post(`/api/content/${id}/duplicate`);
  }
}

// Scheduler Client
class SchedulerClient {
  constructor(client) {
    this.client = client;
  }

  async list(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/scheduler/posts${query ? `?${query}` : ''}`);
  }

  async create(data) {
    return this.client.post('/api/scheduler/posts', data);
  }

  async approve(id) {
    return this.client.post(`/api/scheduler/posts/${id}/approve`);
  }

  async reject(id, reason) {
    return this.client.post(`/api/scheduler/posts/${id}/reject`, { reason });
  }

  async getCalendar(startDate, endDate, filters = {}) {
    const query = new URLSearchParams({ from: startDate, to: endDate, ...filters }).toString();
    // 30s cache — Calendar + Dashboard WeekStrip + ContentPlan all hit this
    return this.client.get(`/api/scheduler/calendar?${query}`, { cacheTTL: 30 * 1000 });
  }

  async aggregateStatus(id) {
    return this.client.post(`/api/scheduler/posts/${id}/status`);
  }

  async update(id, data) {
    return this.client.patch(`/api/scheduler/posts/${id}`, data);
  }

  async reschedule(id, data) {
    return this.client.post(`/api/scheduler/posts/${id}/reschedule`, data);
  }

  async unschedule(id) {
    return this.client.post(`/api/scheduler/posts/${id}/unschedule`);
  }

  async changeTargets(id, data) {
    return this.client.put(`/api/scheduler/posts/${id}/targets`, data);
  }

  async retry(id, data = {}) {
    return this.client.post(`/api/scheduler/posts/${id}/retry`, data);
  }

  async clone(id, data = {}) {
    return this.client.post(`/api/scheduler/posts/${id}/clone`, data);
  }

  async delete(id) {
    return this.client.delete(`/api/scheduler/posts/${id}`);
  }
}

// Social Accounts Client
class SocialAccountsClient {
  constructor(client) {
    this.client = client;
  }

  async list() {
    return this.client.get('/social/accounts', { cacheTTL: 2 * 60 * 1000 });
  }

  async connect(platform, options = {}) {
    if (platform === 'instagram') {
      window.location.assign(this.getInstagramOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'threads') {
      window.location.assign(this.getThreadsOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'meta' || platform === 'facebook') {
      window.location.assign(this.getMetaOAuthStartUrl(platform === 'facebook' ? 'facebook' : 'instagram', options));
      return { redirected: true };
    }
    if (platform === 'tiktok') {
      window.location.assign(this.getTikTokOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'x' || platform === 'twitter') {
      window.location.assign(this.getXOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'telegram') {
      return this.createTelegramLink();
    }
    return this.client.post('/social/connect', { platform });
  }

  async connectInstagramViaFacebook(options = {}) {
    window.location.assign(this.getMetaOAuthStartUrl('instagram', options));
    return { redirected: true };
  }

  getMetaOAuthStartUrl(platform = 'instagram', options = {}) {
    return this.client.url(addQuery('/oauth/meta/start', { platform, ...oauthReturnParams(options) }));
  }

  getInstagramOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/instagram/start', oauthReturnParams(options)));
  }

  getThreadsOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/threads/start', oauthReturnParams(options)));
  }

  getTikTokOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/tiktok/start', oauthReturnParams(options)));
  }

  getXOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/x/start', oauthReturnParams(options)));
  }

  async createTelegramLink() {
    return this.client.post('/social/telegram/link', {});
  }

  async disconnect(id) {
    // Disconnect a single account by id, but invalidate the shared accounts
    // cache (`/social/accounts`) so the next list() returns fresh data.
    return this.client.delete(`/social/${id}`, { invalidate: ['/social/accounts', `/social/${id}`] });
  }

  // Update a single account's settings (currently just publishLanguage).
  // Backend gates per-account language override behind entitlement
  // per_account_language_override (Growth+). Lower plans get 402
  // ENTITLEMENT_REQUIRED with upgradeUrl; the caller should show an upgrade CTA.
  async updateLanguage(id, language) {
    return this.client.request(
      'PATCH',
      `/api/social/accounts/${encodeURIComponent(id)}`,
      { publishLanguage: language },
      { invalidate: ['/social/accounts', `/social/${id}`] },
    );
  }

  async refresh(id) {
    return this.client.post(`/social/${id}/refresh`, null, {
      invalidate: ['/social/accounts', `/social/${id}`],
    });
  }

  async healthCheck(id) {
    return this.client.get(`/social/${id}/health`);
  }
}

function oauthReturnParams(options = {}) {
  const params = {};
  if (options.returnTo) params.return_to = String(options.returnTo);
  if (options.returnPath) params.return_path = String(options.returnPath);
  return params;
}

function addQuery(path, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim()) query.set(key, String(value));
  });
  const serialized = query.toString();
  return serialized ? `${path}?${serialized}` : path;
}

/* Autopilot Client
   Per-user automated weekly cycle: Plan → Compose → Publish → Review →
   Tune → Rest. dathent-autopilot-worker owns the state machine + cron
   trigger; these HTTP methods are only for the UI to read state and
   toggle ON/OFF. */
class AutopilotClient {
  constructor(client) {
    this.client = client;
  }

  async getState() {
    // Short cache — Dashboard polls but doesn't need second-level freshness
    // (cron runs every 15 min anyway).
    return this.client.get('/api/autopilot/state', { cacheTTL: 15 * 1000 });
  }

  async setState({ enabled, timezone, cadence } = {}) {
    return this.client.put('/api/autopilot/state', { enabled, timezone, cadence }, {
      invalidate: ['/api/autopilot/state', '/api/autopilot/runs'],
    });
  }

  async listRuns({ limit = 10 } = {}) {
    return this.client.get(`/api/autopilot/runs?limit=${limit}`, { cacheTTL: 5 * 1000 });
  }
}

// Notifications Client
class NotificationsClient {
  constructor(client) {
    this.client = client;
  }

  async list(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/notifications?${query}`);
  }

  async markRead(id) {
    return this.client.post(`/notifications/${id}/read`);
  }

  async markAllRead() {
    return this.client.post('/notifications/read-all');
  }

  async createRecommendation(recommendation) {
    return this.client.post('/notifications/recommendations', { recommendation });
  }

  async runSmartRecommendations() {
    return this.client.post('/notifications/smart-recommendations/run');
  }

  async dismissRecommendation(recommendationId) {
    return this.client.post(`/notifications/recommendations/${encodeURIComponent(recommendationId)}/dismiss`);
  }

  async getUnreadCount() {
    return this.client.get('/notifications/unread-count');
  }

  async getPreferences() {
    return this.client.get('/notifications/preferences');
  }

  async updatePreferences(preferences) {
    return this.client.put('/notifications/preferences', preferences);
  }
}

// Billing Client
class BillingClient {
  constructor(client) {
    this.client = client;
  }

  async getEntitlements() {
    return this.client.get('/api/billing/entitlements', { cacheTTL: 2 * 60 * 1000 });
  }

  async createCheckoutSession({ plan, interval = 'monthly', successUrl = null, cancelUrl = null }) {
    return this.client.post('/api/billing/checkout', { plan, interval, successUrl, cancelUrl });
  }

  async createPortalSession({ returnUrl = null } = {}) {
    return this.client.post('/api/billing/portal', { returnUrl });
  }
}

// API Keys Client
class ApiKeysClient {
  constructor(client) {
    this.client = client;
  }

  async list() {
    return this.client.get('/api/auth/api-keys');
  }

  async create({ name, scopes, expiresAt = null }) {
    return this.client.post('/api/auth/api-keys', { name, scopes, expiresAt });
  }

  async revoke(id) {
    return this.client.delete(`/api/auth/api-keys/${id}`);
  }
}

// Platforms Client
class PlatformsClient {
  constructor(client) {
    this.client = client;
  }

  async registry() {
    return this.client.get('/api/platforms/registry');
  }

  async get(platform) {
    return this.client.get(`/api/platforms/${encodeURIComponent(platform)}`);
  }
}

// Carousel Client
class CarouselClient {
  constructor(client) {
    this.client = client;
  }

  async generate({ contentItemId, contentVersionId = null, platforms, slideCount }) {
    return this.client.post('/api/carousel/generate', {
      contentItemId,
      contentVersionId,
      platforms,
      slideCount,
    });
  }

  async listSlides(versionId) {
    return this.client.get(`/api/carousel/versions/${encodeURIComponent(versionId)}/slides`);
  }
}

// Assistant Client
class AssistantClient {
  constructor(client) {
    this.client = client;
  }

  async chat({ message, currentScreen, selectedEntity = null, actionMetadata = null }) {
    return this.client.post('/api/assistant/chat', { message, currentScreen, selectedEntity, actionMetadata });
  }

  async messages({ limit = 50 } = {}) {
    return this.client.get(`/api/assistant/messages?limit=${encodeURIComponent(limit)}`);
  }

  async confirmAction({ action, confirmed = true }) {
    return this.client.post('/api/assistant/actions/confirm', { action, confirmed });
  }

  async previewAction({ action }) {
    return this.client.post('/api/assistant/actions/preview', { action, confirmed: false });
  }

  async uploadAttachment(attachment) {
    return this.client.post('/api/assistant/attachments', attachment);
  }
}

// Analytics Client
class AnalyticsClient {
  constructor(client) {
    this.client = client;
  }

  async getInsights({ windowDays = 30 } = {}) {
    return this.client.get(`/api/analytics/insights?window=${encodeURIComponent(windowDays)}`);
  }

  async getPostsSummary({ days = 30 } = {}) {
    return this.client.get(`/api/analytics/posts-summary?days=${encodeURIComponent(days)}`, { cacheTTL: 2 * 60 * 1000 });
  }

  async createRecommendationNotifications({ windowDays = 30, recommendationIds = [] } = {}) {
    return this.client.post('/api/analytics/recommendations/notify', { windowDays, recommendationIds });
  }

  async getBestTimes({ platform, count = 7, windowDays, horizonDays } = {}) {
    const params = new URLSearchParams();
    if (platform) params.set('platform', String(platform).toLowerCase());
    if (count) params.set('count', String(count));
    if (windowDays) params.set('windowDays', String(windowDays));
    if (horizonDays) params.set('horizonDays', String(horizonDays));
    const qs = params.toString();
    return this.client.get(`/api/analytics/best-times${qs ? `?${qs}` : ''}`, { cacheTTL: 60 * 60 * 1000 });
  }
}

// Threads-specific actions for the App Review demo + product features:
// post-level insights, replies feed, reply send, hide/unhide reply, delete.
// All routes scope to the authenticated user via the x-dathent-user-id
// header set by the API gateway.
class ThreadsClient {
  constructor(client) {
    this.client = client;
  }

  async getInsights(postId) {
    // Cached briefly — reviewers refresh insights button shouldn't hammer
    // Threads' rate-limited insights endpoint.
    return this.client.get(`/api/threads/posts/${encodeURIComponent(postId)}/insights`, { cacheTTL: 30 * 1000 });
  }

  async listReplies(postId) {
    return this.client.get(`/api/threads/posts/${encodeURIComponent(postId)}/replies`, { cacheTTL: 15 * 1000 });
  }

  async sendReply(postId, text) {
    return this.client.post(`/api/threads/posts/${encodeURIComponent(postId)}/reply`, { text }, {
      invalidate: [`/api/threads/posts/${encodeURIComponent(postId)}/replies`],
    });
  }

  async hideReply(replyId) {
    return this.client.post(`/api/threads/replies/${encodeURIComponent(replyId)}/hide`, null);
  }

  async unhideReply(replyId) {
    return this.client.post(`/api/threads/replies/${encodeURIComponent(replyId)}/unhide`, null);
  }

  async deletePost(postId) {
    return this.client.delete(`/api/threads/posts/${encodeURIComponent(postId)}`);
  }
}

// Instagram Business client — mirrors Threads. Uses Meta Graph API for IG
// Business / Creator accounts. All routes scope to the authenticated user
// via the x-dathent-user-id header set by the API gateway.
class InstagramClient {
  constructor(client) {
    this.client = client;
  }

  async getInsights(postId) {
    return this.client.get(`/api/instagram/posts/${encodeURIComponent(postId)}/insights`, { cacheTTL: 30 * 1000 });
  }

  async listComments(postId) {
    return this.client.get(`/api/instagram/posts/${encodeURIComponent(postId)}/comments`, { cacheTTL: 15 * 1000 });
  }

  async sendComment(postId, text) {
    return this.client.post(`/api/instagram/posts/${encodeURIComponent(postId)}/comment`, { text }, {
      invalidate: [`/api/instagram/posts/${encodeURIComponent(postId)}/comments`],
    });
  }

  async replyToComment(commentId, text) {
    return this.client.post(`/api/instagram/comments/${encodeURIComponent(commentId)}/reply`, { text });
  }

  async hideComment(commentId) {
    return this.client.post(`/api/instagram/comments/${encodeURIComponent(commentId)}/hide`, null);
  }

  async unhideComment(commentId) {
    return this.client.post(`/api/instagram/comments/${encodeURIComponent(commentId)}/unhide`, null);
  }

  async deletePost(postId) {
    return this.client.delete(`/api/instagram/posts/${encodeURIComponent(postId)}`);
  }

  // Direct Messages (instagram_business_manage_messages)
  async listConversations(accountId) {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.client.get(`/api/instagram/conversations${qs}`, { cacheTTL: 10 * 1000 });
  }

  async getConversation(conversationId, accountId) {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.client.get(`/api/instagram/conversations/${encodeURIComponent(conversationId)}${qs}`, { cacheTTL: 5 * 1000 });
  }

  async sendMessage(conversationId, text, accountId) {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.client.post(`/api/instagram/conversations/${encodeURIComponent(conversationId)}/messages${qs}`, { text }, {
      invalidate: [
        `/api/instagram/conversations/${encodeURIComponent(conversationId)}`,
        `/api/instagram/conversations`,
      ],
    });
  }
}

// Referral Client — referrer profile + lead-capture signup + payouts
class ReferralClient {
  constructor(client) {
    this.client = client;
  }

  async lookupByCode(code) {
    return this.client.get(`/api/referral/r/${encodeURIComponent(code)}`, { cacheTTL: 30 * 1000 });
  }

  async submitSignup({ code, email, contactKind, contactValue, locale, utm }) {
    return this.client.post('/api/referral/signup', {
      code,
      email,
      contactKind,
      contactValue,
      locale,
      utm,
    });
  }

  async getMyProfile() {
    return this.client.get('/api/referral/me');
  }

  async upsertMyProfile({ code, regenerateCode, displayName, studio, photoUrl, photoPosition, avatarGradient, customMessage } = {}) {
    return this.client.post('/api/referral/me', {
      code,
      regenerateCode,
      displayName,
      studio,
      photoUrl,
      photoPosition,
      avatarGradient,
      customMessage,
    }, { invalidate: ['/api/referral/me'] });
  }

  async uploadPhoto(file) {
    if (!(file instanceof Blob)) {
      throw new DathentAPIError('uploadPhoto requires a File or Blob', 'VALIDATION_ERROR', 400);
    }
    const contentType = file.type || 'application/octet-stream';
    const url = `${this.client.baseURL}/api/referral/photo`;
    const headers = { 'Content-Type': contentType };
    const token = this.client.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: file,
      credentials: 'include',
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      const apiError = json?.error || {};
      throw new DathentAPIError(
        apiError.message || json?.message || 'Photo upload failed',
        apiError.code || 'UPLOAD_FAILED',
        response.status,
        apiError.details || null,
      );
    }
    this.client.invalidateCache('/api/referral/me');
    return json?.ok === true && Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json;
  }

  async listSignups() {
    return this.client.get('/api/referral/me/signups');
  }

  async listPayouts() {
    return this.client.get('/api/referral/me/payouts');
  }

  async requestPayout({ amountCents, method, methodDetails, notes } = {}) {
    return this.client.post('/api/referral/me/payouts/request', {
      amountCents,
      method,
      methodDetails,
      notes,
    }, { invalidate: ['/api/referral/me/payouts'] });
  }
}

// Admin Referral Client — operator review (gated by email allowlist server-side)
class AdminReferralClient {
  constructor(client) {
    this.client = client;
  }

  async stats() {
    return this.client.get('/api/admin/referrals/stats');
  }

  async listSignups({ status = 'all', limit = 500 } = {}) {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    q.set('limit', String(limit));
    return this.client.get(`/api/admin/referrals/signups?${q.toString()}`);
  }

  async listPayouts({ status = 'all', limit = 200 } = {}) {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    q.set('limit', String(limit));
    return this.client.get(`/api/admin/referrals/payouts?${q.toString()}`);
  }

  async markPayoutPaid(id, { method, methodDetails, notes } = {}) {
    return this.client.post(
      `/api/admin/referrals/payouts/${encodeURIComponent(id)}/mark-paid`,
      { method, methodDetails, notes },
      { invalidate: ['/api/admin/referrals/payouts'] }
    );
  }
}

// Export singleton instance
export const api = new DathentClient();

export default DathentClient;
